// CustomFieldsManagerPage — Program Tanımları altında.
// Entity tipini seç → alanları listele/yarat/düzenle/sil/sırala.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from '../components/Toast';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const ENTITY_TYPES = [
  { key: 'account',          label: 'Cari Hesap' },
  { key: 'prospect_account', label: 'Potansiyel Cari' },
  { key: 'stock',            label: 'Stok / Ürün' },
  { key: 'sales_invoice',    label: 'Satış Faturası' },
  { key: 'purchase_invoice', label: 'Alış Faturası' },
  { key: 'sales_order',      label: 'Satış Siparişi' },
  { key: 'purchase_order',   label: 'Alış Siparişi' },
  { key: 'sales_quote',      label: 'Satış Teklifi' },
  { key: 'purchase_quote',   label: 'Alış Teklifi' },
  { key: 'sales_waybill',    label: 'Satış İrsaliyesi' },
  { key: 'purchase_waybill', label: 'Alış İrsaliyesi' },
  { key: 'personnel',        label: 'Personel' },
  { key: 'appointment',      label: 'Randevu' },
  { key: 'warehouse',        label: 'Depo' },
  { key: 'bank_account',     label: 'Banka Hesabı' },
  { key: 'check_note',       label: 'Çek / Senet' },
  { key: 'project',          label: 'Proje' },
];

const FIELD_TYPES = [
  { value: 'text',        label: 'Kısa Metin' },
  { value: 'textarea',    label: 'Uzun Metin' },
  { value: 'number',      label: 'Tam Sayı' },
  { value: 'decimal',     label: 'Ondalık Sayı' },
  { value: 'date',        label: 'Tarih' },
  { value: 'datetime',    label: 'Tarih + Saat' },
  { value: 'boolean',     label: 'Onay Kutusu' },
  { value: 'select',      label: 'Açılır Liste' },
  { value: 'multiselect', label: 'Çoklu Seçim' },
  { value: 'url',         label: 'Web Adresi' },
  { value: 'email',       label: 'E-posta' },
  { value: 'phone',       label: 'Telefon' },
];

const TYPE_LABELS = Object.fromEntries(FIELD_TYPES.map(t => [t.value, t.label]));

const EMPTY_FORM = {
  entity_type: 'account',
  label: '',
  field_key: '',
  field_type: 'text',
  options_json: '',
  is_required: false,
  is_searchable: false,
  default_value: '',
  placeholder: '',
  help_text: '',
  group_label: '',
  sort_order: 100,
};

export default function CustomFieldsManagerPage() {
  const [entityType, setEntityType] = useState('account');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/custom-fields/definitions', { params: { entity_type: entityType } });
      if (r.data.success) setList(r.data.data || []);
    } catch (e) {
      toast.error('Liste yüklenemedi');
    } finally { setLoading(false); }
  }, [entityType]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, entity_type: entityType, sort_order: (list.length + 1) * 10 });
    setShowModal(true);
  };

  const handleEdit = (def) => {
    setEditingId(def.id);
    setForm({
      entity_type: def.entity_type,
      label: def.label || '',
      field_key: def.field_key || '',
      field_type: def.field_type || 'text',
      options_json: def.options_json ? formatOptions(def.options_json) : '',
      is_required: !!def.is_required,
      is_searchable: !!def.is_searchable,
      default_value: def.default_value || '',
      placeholder: def.placeholder || '',
      help_text: def.help_text || '',
      group_label: def.group_label || '',
      sort_order: def.sort_order || 100,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Etiket zorunlu'); return; }
    setSaving(true);
    try {
      const payload = {
        entity_type: form.entity_type,
        label: form.label.trim(),
        field_key: form.field_key?.trim() || undefined,
        field_type: form.field_type,
        options_json: parseOptionsInput(form.options_json),
        is_required: !!form.is_required,
        is_searchable: !!form.is_searchable,
        default_value: form.default_value || null,
        placeholder: form.placeholder || null,
        help_text: form.help_text || null,
        group_label: form.group_label || null,
        sort_order: parseInt(form.sort_order) || 100,
      };
      if (editingId) {
        await api.put('/custom-fields/definitions/' + editingId, payload);
        toast.success('Güncellendi');
      } else {
        await api.post('/custom-fields/definitions', payload);
        toast.success('Eklendi');
      }
      setShowModal(false);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Kayıt başarısız');
    } finally { setSaving(false); }
  };

  const handleDelete = async (def) => {
    if (!window.confirm(`"${def.label}" alanı silinsin mi? (mevcut kayıtlardaki veri saklanır, sadece form alanı kalkar)`)) return;
    try {
      await api.delete('/custom-fields/definitions/' + def.id);
      toast.success('Silindi');
      await fetchList();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const needsOptions = form.field_type === 'select' || form.field_type === 'multiselect';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Özel Alanlar"
        unit="özel alan"
        count={list.length}
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Alan</Button>}
      />

      <div style={{ padding: 12, background: 'var(--bg-card-hover)', borderBottom: '1px solid var(--border-secondary)' }}>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 6, fontWeight: 600 }}>
          MODÜL SEÇİN
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ENTITY_TYPES.map(et => (
            <button
              key={et.key}
              type="button"
              onClick={() => setEntityType(et.key)}
              style={{
                padding: '5px 12px',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                border: '1px solid ' + (entityType === et.key ? 'var(--accent-primary)' : 'var(--border-input)'),
                background: entityType === et.key ? 'var(--accent-glow)' : 'var(--bg-card)',
                color: entityType === et.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
                borderRadius: 'var(--r-full)',
                cursor: 'pointer',
              }}
            >{et.label}</button>
          ))}
        </div>
      </div>

      {loading && list.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>Yükleniyor...</div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="✨"
          title="Henüz özel alan yok"
          description={`"${ENTITY_TYPES.find(e => e.key === entityType)?.label}" için "+ Yeni Alan" butonuyla ekleyebilirsiniz.`}
        />
      ) : (
        <div style={{ padding: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {list.map(def => (
              <div key={def.id} style={S.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  <span style={S.typeBadge}>{TYPE_LABELS[def.field_type] || def.field_type}</span>
                  {def.is_required && <span style={{ ...S.typeBadge, background: 'var(--danger-bg)', color: 'var(--danger)' }}>Zorunlu</span>}
                  {def.is_searchable && <span style={{ ...S.typeBadge, background: 'var(--info-bg)', color: 'var(--info)' }}>Aranabilir</span>}
                </div>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                  {def.label}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontFamily: 'JetBrains Mono, monospace', marginBottom: 8 }}>
                  {def.field_key}
                </div>
                {def.group_label && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Grup: <strong>{def.group_label}</strong>
                  </div>
                )}
                {def.help_text && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 6, fontStyle: 'italic' }}>
                    {def.help_text}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                  <Button size="xs" variant="ghost" onClick={() => handleEdit(def)}>✎ Düzenle</Button>
                  <Button size="xs" variant="ghost" onClick={() => handleDelete(def)}>🗑 Sil</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingId ? 'Alanı Düzenle' : 'Yeni Özel Alan'}
          size="md"
          onClose={() => setShowModal(false)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowModal(false)}>İptal</Button>
              <Button onClick={handleSave} loading={saving}>💾 Kaydet</Button>
            </div>
          }
        >
          <div className="form-grid">
            <FormField label="Modül">
              <select value={form.entity_type} onChange={e => setForm({ ...form, entity_type: e.target.value })} disabled={!!editingId}>
                {ENTITY_TYPES.map(et => <option key={et.key} value={et.key}>{et.label}</option>)}
              </select>
            </FormField>
            <FormField label="Etiket (kullanıcının gördüğü)" required>
              <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="örn. Top No" />
            </FormField>
            <FormField label="Anahtar (sistem)" hint={editingId ? 'Mevcut alanın anahtarı değiştirilemez' : 'Boş bırakırsanız etiketten üretilir'}>
              <input value={form.field_key} onChange={e => setForm({ ...form, field_key: e.target.value })} placeholder="otomatik" disabled={!!editingId} />
            </FormField>
            <FormField label="Tip">
              <select value={form.field_type} onChange={e => setForm({ ...form, field_type: e.target.value })}>
                {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FormField>
            {needsOptions && (
              <FormField label="Seçenekler" fullWidth hint="Her satıra bir seçenek. veya 'value|Görünen Ad' formatı kullanın.">
                <textarea
                  rows={4}
                  value={form.options_json}
                  onChange={e => setForm({ ...form, options_json: e.target.value })}
                  placeholder="Kırmızı&#10;Mavi&#10;Yeşil"
                />
              </FormField>
            )}
            <FormField label="Grup">
              <input value={form.group_label} onChange={e => setForm({ ...form, group_label: e.target.value })} placeholder="örn. Tekstil Bilgileri" />
            </FormField>
            <FormField label="Sıra No"><input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} /></FormField>
            <FormField label="Placeholder"><input value={form.placeholder} onChange={e => setForm({ ...form, placeholder: e.target.value })} /></FormField>
            <FormField label="Varsayılan Değer"><input value={form.default_value} onChange={e => setForm({ ...form, default_value: e.target.value })} /></FormField>
            <FormField label="Yardım Metni" fullWidth>
              <input value={form.help_text} onChange={e => setForm({ ...form, help_text: e.target.value })} placeholder="Alanın altında küçük yazıyla gösterilir" />
            </FormField>
            <FormField label="Seçenekler" fullWidth>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.is_required} onChange={e => setForm({ ...form, is_required: e.target.checked })} />
                  Zorunlu
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.is_searchable} onChange={e => setForm({ ...form, is_searchable: e.target.checked })} />
                  Aranabilir (Filter'da görünsün)
                </label>
              </div>
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}

// 'Kırmızı\nMavi\nYeşil' veya 'kr|Kırmızı\nmv|Mavi' biçimini JSON'a dönüştür
function parseOptionsInput(raw) {
  if (!raw) return null;
  const lines = String(raw).split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  return lines.map(line => {
    if (line.includes('|')) {
      const [v, l] = line.split('|').map(s => s.trim());
      return { value: v, label: l || v };
    }
    return { value: line, label: line };
  });
}

// JSON → çok satırlı text
function formatOptions(json) {
  let arr;
  try { arr = typeof json === 'string' ? JSON.parse(json) : json; }
  catch { return ''; }
  if (!Array.isArray(arr)) return '';
  return arr.map(o => {
    if (typeof o === 'string') return o;
    if (o.value === o.label || !o.label) return o.value;
    return o.value + '|' + o.label;
  }).join('\n');
}

const S = {
  card: {
    padding: 12,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    boxShadow: 'var(--shadow-xs)',
  },
  typeBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 'var(--r-full)',
    background: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
};
